import React, { useEffect, useState } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Calendar, Plus, Loader2, Trash2, Play, Pencil, Users, DollarSign, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function PayrollSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [runConfirm, setRunConfirm] = useState(null);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Create form
  const [formName, setFormName] = useState('');
  const [formCycle, setFormCycle] = useState('monthly');
  const [formItems, setFormItems] = useState([]);

  const loadSchedules = async () => {
    try { setSchedules(await companyAdminAPI.getPayrollSchedules()); } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    loadSchedules();
    companyAdminAPI.getEmployees().then(setEmployees).catch(() => {});
  }, []);

  const openCreate = () => {
    setFormName('');
    setFormCycle('monthly');
    setFormItems(employees.map(e => ({ employee_id: e.id, name: `${e.first_name} ${e.last_name}`, email: e.email, amount: '', included: false })));
    setEditMode(false);
    setCreateOpen(true);
  };

  const openEdit = async (schedule) => {
    try {
      const data = await companyAdminAPI.getPayrollSchedule(schedule.id);
      setFormName(data.name);
      setFormCycle(data.cycle_type);
      const existingMap = {};
      data.items.forEach(i => { existingMap[i.employee_id] = i.amount; });
      setFormItems(employees.map(e => ({
        employee_id: e.id,
        name: `${e.first_name} ${e.last_name}`,
        email: e.email,
        amount: existingMap[e.id] !== undefined ? String(existingMap[e.id]) : '',
        included: existingMap[e.id] !== undefined,
      })));
      setEditMode(true);
      setDetailOpen(schedule.id);
      setCreateOpen(true);
    } catch { toast.error('Failed to load schedule'); }
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Name is required'); return; }
    const items = formItems.filter(i => i.included && parseFloat(i.amount) > 0).map(i => ({
      employee_id: i.employee_id, amount: parseFloat(i.amount),
    }));
    if (!items.length) { toast.error('Add at least one employee with an amount'); return; }

    setSubmitting(true);
    try {
      if (editMode && detailOpen) {
        await companyAdminAPI.updatePayrollSchedule(detailOpen, { name: formName, cycle_type: formCycle, items });
        toast.success('Schedule updated');
      } else {
        await companyAdminAPI.createPayrollSchedule({ name: formName, cycle_type: formCycle, items });
        toast.success('Schedule created');
      }
      setCreateOpen(false);
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await companyAdminAPI.deletePayrollSchedule(id); toast.success('Schedule deleted'); loadSchedules(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRun = async (schedule) => {
    setRunning(true);
    try {
      const res = await companyAdminAPI.runPayrollSchedule(schedule.id);
      toast.success(`Payroll complete: ${res.total_paid} paid, NGN ${res.total_amount?.toLocaleString()}`);
      if (res.total_failed > 0) toast.warning(`${res.total_failed} payments failed`);
      setRunConfirm(null);
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payroll failed'); }
    finally { setRunning(false); }
  };

  const viewDetail = async (id) => {
    try { setDetailData(await companyAdminAPI.getPayrollSchedule(id)); setDetailOpen(id); }
    catch { toast.error('Failed to load'); }
  };

  const fmt = (n) => `NGN ${Number(n || 0).toLocaleString()}`;

  const toggleAll = (val) => setFormItems(prev => prev.map(i => ({ ...i, included: val })));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-5" data-testid="payroll-schedules">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payroll Schedules</h1>
          <p className="text-sm text-gray-500">{schedules.length} schedule{schedules.length !== 1 ? 's' : ''} configured</p>
        </div>
        <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={openCreate} data-testid="create-schedule-btn">
          <Plus className="w-4 h-4 mr-2" /> New Schedule
        </Button>
      </div>

      {/* Schedule Cards */}
      {schedules.length === 0 ? (
        <Card className="border-dashed border-gray-300">
          <CardContent className="p-8 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-2">No payroll schedules yet</p>
            <p className="text-xs text-gray-400 mb-4">Create a schedule to set up recurring payment cycles for your employees</p>
            <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={openCreate}>Create First Schedule</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {schedules.map(s => (
            <Card key={s.id} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`schedule-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => viewDetail(s.id)}>
                    <div className="w-10 h-10 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                      <p className="text-xs text-gray-500">
                        <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{s.cycle_type}</span>
                        <span className="ml-2">{s.employee_count} employee{s.employee_count !== 1 ? 's' : ''}</span>
                        <span className="ml-2 font-mono font-bold">{fmt(s.total_amount)}</span>
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 ml-auto" />
                  </div>
                  <div className="flex gap-2 ml-3">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEdit(s)} data-testid={`edit-schedule-${s.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" className="h-8 text-xs bg-gray-300 text-gray-500 cursor-not-allowed" disabled title="Payroll schedules are temporarily disabled. Please use the standard payroll disbursement flow." data-testid={`run-schedule-${s.id}`}>
                      <Play className="w-3 h-3 mr-1" /> Run Payroll
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-500 hover:bg-red-50" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'New'} Payroll Schedule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Schedule Name</Label><Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Monthly Salary, Bi-Weekly Pay" className="h-9" data-testid="schedule-name-input" /></div>
              <div>
                <Label className="text-sm">Cycle Type</Label>
                <Input value={formCycle} onChange={e => setFormCycle(e.target.value)} placeholder="e.g. monthly, bi-weekly, weekly" className="h-9" data-testid="schedule-cycle-input" />
              </div>
            </div>

            {/* Employee Amounts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Employee Amounts</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleAll(true)}>Select All</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleAll(false)}>Deselect All</Button>
                </div>
              </div>
              <div className="space-y-1.5 max-h-[40vh] overflow-y-auto border rounded-lg p-2">
                {formItems.map((item, idx) => (
                  <div key={item.employee_id} className={`flex items-center gap-2 p-2 rounded-lg ${item.included ? 'bg-pink-50/50' : 'bg-gray-50'}`}>
                    <input type="checkbox" checked={item.included} onChange={e => {
                      const updated = [...formItems]; updated[idx].included = e.target.checked; setFormItems(updated);
                    }} className="w-4 h-4 accent-pink-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{item.email}</p>
                    </div>
                    <div className="relative w-32">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">NGN</span>
                      <Input type="number" value={item.amount} onChange={e => {
                        const updated = [...formItems]; updated[idx].amount = e.target.value; updated[idx].included = true; setFormItems(updated);
                      }} placeholder="0" className="h-8 pl-9 text-xs font-mono" style={{ fontFamily: 'JetBrains Mono' }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2 text-right">
                Total: <span className="font-bold font-mono">{fmt(formItems.filter(i => i.included).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))}</span>
                <span className="ml-3">{formItems.filter(i => i.included && parseFloat(i.amount) > 0).length} employees</span>
              </p>
            </div>

            <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-10" onClick={handleSave} disabled={submitting} data-testid="save-schedule-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editMode ? 'Save Changes' : 'Create Schedule')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailData && !createOpen} onOpenChange={() => setDetailData(null)}>
        <DialogContent className="border-gray-200 max-w-lg max-h-[80vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader><DialogTitle>{detailData.name}</DialogTitle></DialogHeader>
              <div className="flex gap-3 mb-3">
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">{detailData.cycle_type}</span>
                <span className="text-xs text-gray-500">{detailData.employee_count} employees</span>
                <span className="text-xs font-bold font-mono">{fmt(detailData.total_amount)}</span>
              </div>
              <div className="space-y-1">
                {detailData.items?.map(i => (
                  <div key={i.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-gray-900">{i.employee_name}</p>
                      <p className="text-[10px] text-gray-400">{i.employee_email}</p>
                    </div>
                    <span className="text-xs font-bold font-mono">{fmt(i.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Run Confirmation */}
      <Dialog open={!!runConfirm} onOpenChange={() => setRunConfirm(null)}>
        <DialogContent className="border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Run Payroll</DialogTitle></DialogHeader>
          {runConfirm && (
            <>
              <p className="text-sm text-gray-600">
                This will pay <strong>{runConfirm.employee_count} employees</strong> a total of <strong className="font-mono">{fmt(runConfirm.total_amount)}</strong> from your company wallet.
              </p>
              <p className="text-xs text-gray-400 mt-1">Schedule: {runConfirm.name} ({runConfirm.cycle_type})</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="flex-1" onClick={() => setRunConfirm(null)} disabled={running}>Cancel</Button>
                <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleRun(runConfirm)} disabled={running} data-testid="confirm-run-btn">
                  {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  Confirm & Pay
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
